/**
 * Brick colour family — copied from the particle lab (profiles.ts /
 * chunkSim.ts), never imported. Weathered faces sit on the muted terracotta
 * pile/chunk tint; freshly exposed interiors use a brighter salmon clay.
 */

export type Rgb = readonly [number, number, number];

/** Particle-lab pile / heightfield brick tint. */
export const TERRACOTTA: Rgb = [0.5, 0.22, 0.15];
/** Particle-lab whole-brick chunk base. */
export const TERRACOTTA_CHUNK: Rgb = [0.55, 0.22, 0.14];
/** Particle-lab crater-source clay (darker crushed brick). */
export const CLAY_CRUSHED: Rgb = [0.46, 0.24, 0.16];
/** Brighter salmon — interior reveal on a broken face. */
export const CLAY_SALMON: Rgb = [0.78, 0.42, 0.26];
/** Inner-rim / depth cue inside a spall patch. */
export const CLAY_DEEP: Rgb = [0.42, 0.2, 0.13];
/** Particle-lab mortar cloud. */
export const MORTAR: Rgb = [0.36, 0.34, 0.31];
/** Particle-lab chunk mortar crust. */
export const MORTAR_CRUST: Rgb = [0.42, 0.4, 0.37];
/** Wet bleed stain (grey, slightly darker than mortar). */
export const STAIN_GREY: Rgb = [0.28, 0.26, 0.24];
/** Missile soot. */
export const SOOT: Rgb = [0.07, 0.06, 0.055];

export function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return Math.min(1, Math.max(0, x));
}

/** Seeded weathered-face tint around TERRACOTTA. Occasional flashed header. */
export function weatheredTint(t0: number, t1: number, t2: number): [number, number, number] {
  const a = clamp01(t0);
  const b = clamp01(t1);
  const c = clamp01(t2);
  if (a > 0.92 && b < 0.38) {
    return [
      clamp01(0.34 + a * 0.055),
      clamp01(0.14 + b * 0.05),
      clamp01(0.11 + c * 0.03),
    ];
  }
  const warm = (a - c) * 0.028;
  return [
    clamp01(0.445 + a * 0.11 + warm),
    clamp01(0.175 + b * 0.08),
    clamp01(0.115 + c * 0.045),
  ];
}

export function rgbToCss(rgb: Rgb, a = 1): string {
  const r = Math.round(clamp01(rgb[0]) * 255);
  const g = Math.round(clamp01(rgb[1]) * 255);
  const b = Math.round(clamp01(rgb[2]) * 255);
  return a >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`;
}
